// Browser polyfills
import '@babel/polyfill';
import 'whatwg-fetch';

// Libs
import React from 'react';
import { render } from 'react-dom';
import { Provider, connect } from 'react-redux';
import { Layout, Result, Spin } from '@nackle/paper';
import { AppHeader, AppFooter } from '@nackle/studio-tools';
import NackleMenu from '@nackle/menu';
const { Content } = Layout;

// Routing
import { Route, routeHistory, Link } from '@nackle/biw-web-router';
import { routes, bundleRoutingMiddleware } from './app-routing.jsx';

// Bridge
import { Bridge } from '@nackle/bridge';
import auth, { authRouteMiddleware, authRequestMiddleware } from './app-auth.js';

import AppStore from './app-store';

import { loadBundles, setUserDetails } from './app-dux/app-dux';

import ErrorBoundary from './components/shared/error-boundary/error-boundary.jsx';

// Fetch
import { applyRequestMiddleware } from './utils/fetch';

import './styles/style.less';

/*******************************************************************************
 *	1. App (root level component)
 */
class App extends React.Component {
	state = {
		loaded: false,
		cmxBundleLoaded: false
	};

	handleSignIn = async ( authState ) => {
		const { loadBundles } = this.props;
		const projectName = 'navigation';

		await routeHistory.refresh();
		await this.props.setUserDetails( authState?.claims );
		try {
			await loadBundles( [ projectName ] );
			this.setState( {
				loaded: true,
				cmxBundleLoaded: true
			} );
		} catch ( err ) {
			console.error( `Error While trying to fetch bundle data from project ${ projectName }`, err );
			this.setState( {
				loaded: true,
				cmxBundleLoaded: false
			} );
		}
	};

	handleCompanyChange = async company => {
        await auth.delegate( company.id );
        routeHistory.push( `/${ company.id }` );
    };


	render() {
		const {
			loaded,
			cmxBundleLoaded
		} = this.state;

		const { loading } = this.props;

		return (
			<ErrorBoundary>
				<Route render={ ( route ) => {
					const companyId = route.state?.params?.companyId;

					return (
						<Bridge
							auth={ auth }
							onSignIn={ this.handleSignIn }
						>
							{
								loaded && (
									<Layout className="root-layout">
										<AppHeader 
											companyId={ companyId }
											auth={ auth }
											onCompanyChange={ this.handleCompanyChange }
										/>
										<Layout>
											<NackleMenu
												auth={ auth }
												cmxBundleLoaded={ cmxBundleLoaded }
											/>
											<Layout className="page-layout">
												{
													loading &&
													<div className="loading-overlay">
														<div className="loading-indicator">
															<Spin size="large" />
														</div>
													</div>
												}
												<Content className="app-content">
													{ route.match ?
														React.cloneElement( route.component, { companyId } )
														:
														(
															<Result
																status="404"
																title="404"
																subTitle="Sorry, the page you visited does not exist."
																extra={ <Link to="/" className="ant-btn ant-btn-primary">Back Home</Link> }
															/>
														)
													}
												</Content>
												<AppFooter/>
											</Layout>
										</Layout>
									</Layout>
								)
							}
						</Bridge>
					);
				} }/>
			</ErrorBoundary>
		);
	}
}


/*******************************************************************************
 *	2. Connect Routing
 */

App = connect(
	( state ) => {
		return {
			loading: state.app.loading
		};
	},
	{
		loadBundles,
		setUserDetails
	}
)( App );

/*******************************************************************************
 *  3. Connect Routing
 */
routeHistory.applyRoutes( routes );
routeHistory.applyMiddleware( [ authRouteMiddleware, bundleRoutingMiddleware ] );

/*******************************************************************************
 *  4. Connect Auth to Fetch
 */
applyRequestMiddleware( authRequestMiddleware );


/*******************************************************************************
 * 5. Render the the app
 */
render(
	<Provider store={ AppStore }>
		<App />
	</Provider>,
	document.getElementById( 'app-root' )
);

// Conditionally enable hot module reloading
if ( module.hot ) {
	module.hot.accept();
}